import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useState } from 'react'
import {
    CircleCheck,
    TrendingDown,
    TrendingUp,
    TriangleAlert
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { CountUp } from '@/components/generals/count-up'
import { SectionBadge } from '@/components/landing/section-badge'
import { useInViewTrigger } from '@/hooks/use-in-view-trigger'

type RangeKey = '7d' | '30d' | '90d'

type RangeConfig = {
    label: string
    comparison: string
    days: number
    bucket: 'day' | 'week'
    success: number
    successDelta: number
    cost: number
    costDelta: number
}

type Agent = {
    name: string
    team: string
    share: number
    success: number
    costPerRun: number
    status: 'healthy' | 'review'
}

type Bar = {
    label: string
    value: number
}

const RANGES: Record<RangeKey, RangeConfig> = {
    '7d': { label: '7 days', comparison: 'vs previous 7 days', days: 7, bucket: 'day', success: 99.4, successDelta: 0.2, cost: 0.029, costDelta: -6 },
    '30d': { label: '30 days', comparison: 'vs previous 30 days', days: 30, bucket: 'day', success: 99.2, successDelta: 0.6, cost: 0.031, costDelta: -12 },
    '90d': { label: '90 days', comparison: 'vs previous 90 days', days: 91, bucket: 'week', success: 98.9, successDelta: 1.1, cost: 0.034, costDelta: -18 }
}

const RANGE_KEYS: RangeKey[] = ['7d', '30d', '90d']

const AGENTS: Agent[] = [
    { name: 'Support Triage', team: 'Customer success', share: 0.38, success: 99.5, costPerRun: 0.024, status: 'healthy' },
    { name: 'Lead Enricher', team: 'Sales', share: 0.22, success: 99.4, costPerRun: 0.031, status: 'healthy' },
    { name: 'Invoice Reconciler', team: 'Finance', share: 0.16, success: 99.7, costPerRun: 0.028, status: 'healthy' },
    { name: 'Contract Reviewer', team: 'Legal', share: 0.13, success: 98.9, costPerRun: 0.052, status: 'healthy' },
    { name: 'Churn Watch', team: 'Customer success', share: 0.11, success: 97.6, costPerRun: 0.036, status: 'review' }
]

// Deterministic sample activity, so the server and the browser draw the same dashboard.
const seeded = (seed: number) => {
    let state = seed

    return () => {
        state = (state + 0x6d2b79f5) | 0

        let t = Math.imul(state ^ (state >>> 15), state | 1)

        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const random = seeded(20260930)

const DAILY = Array.from({ length: 182 }, (_, index) => {
    const weekend = (index + 2) % 7 >= 5 ? 0.45 : 1
    const growth = 1 + 0.0018 * index
    const noise = 0.94 + random() * 0.12

    return Math.round(1600 * growth * weekend * noise)
})

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)

const change = (current: number, previous: number) => ((current - previous) / previous) * 100

const toWeeks = (values: number[]) => Array.from({ length: Math.ceil(values.length / 7) }, (_, week) => sum(values.slice(week * 7, week * 7 + 7)))

const relativeLabel = (ago: number, bucket: 'day' | 'week') => {
    if (bucket === 'week') {
        if (ago === 0) return 'This week'
        if (ago === 1) return 'Last week'

        return `${ago} weeks ago`
    }

    if (ago === 0) return 'Today'
    if (ago === 1) return 'Yesterday'

    return `${ago} days ago`
}

const buildRange = (key: RangeKey) => {
    const range = RANGES[key]
    const current = DAILY.slice(-range.days)
    const previous = DAILY.slice(-range.days * 2, -range.days)
    const tasks = sum(current)
    const previousTasks = sum(previous)
    const values = range.bucket === 'day' ? current : toWeeks(current)
    const bars: Bar[] = values.map((value, index) => ({
        value,
        label: relativeLabel(values.length - 1 - index, range.bucket)
    }))

    return {
        tasks,
        tasksDelta: change(tasks, previousTasks),
        hours: Math.round((tasks * 1.45) / 60),
        hoursDelta: change(tasks, previousTasks),
        bars
    }
}

const DATA: Record<RangeKey, ReturnType<typeof buildRange>> = {
    '7d': buildRange('7d'),
    '30d': buildRange('30d'),
    '90d': buildRange('90d')
}

const niceScale = (max: number) => {
    const rough = max / 3
    const magnitude = 10 ** Math.floor(Math.log10(rough))
    const normalized = rough / magnitude
    const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10
    const step = factor * magnitude
    const top = Math.ceil(max / step) * step
    const ticks: number[] = []

    for (let tick = 0; tick <= top; tick += step) ticks.push(tick)

    return { top, ticks }
}

const formatNumber = (value: number) => value.toLocaleString('en-US')

const formatDelta = (value: number, unit: '%' | ' pts') => `${value > 0 ? '+' : ''}${value.toFixed(1)}${unit}`

interface StatTileProps {
    label: string
    value: string
    delta: string
    comparison: string
    direction: 'up' | 'down'
}

const StatTile: FC<StatTileProps> = ({ label, value, delta, comparison, direction }): ReactNode => {
    const Icon = direction === 'up' ? TrendingUp : TrendingDown

    return (
        <div className='rounded-[22px] border border-border bg-white px-5 py-4.5'>
            <dt className='text-[13px] text-muted-foreground'>
                {label}
            </dt>

            <dd className='mt-1.5 font-heading text-[clamp(1.6rem,2.3vw,2rem)] leading-tight font-semibold tracking-[-0.03em] text-ink'>
                <CountUp value={value} duration={1200} />
            </dd>

            <dd className='mt-1 flex flex-wrap items-center gap-x-1.5 text-[12.5px]'>
                <span className='inline-flex items-center gap-1 font-semibold text-moss'>
                    <Icon aria-hidden className='size-3.5' />
                    {delta}
                </span>

                <span className='text-muted-foreground'>
                    {comparison}
                </span>
            </dd>
        </div>
    )
}

interface ActivityChartProps {
    bars: Bar[]
    bucket: 'day' | 'week'
}

const ActivityChart: FC<ActivityChartProps> = ({ bars, bucket }): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const [chartRef, chartState] = useInViewTrigger<HTMLElement>()

    const { top, ticks } = niceScale(Math.max(...bars.map((bar) => bar.value)))
    const current = active !== null ? bars[active] : null
    const title = bucket === 'day' ? 'Tasks completed per day' : 'Tasks completed per week'

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') return

        event.preventDefault()

        setActive((index) => {
            const start = index ?? bars.length - 1

            if (event.key === 'Home') return 0
            if (event.key === 'End') return bars.length - 1
            if (event.key === 'ArrowLeft') return Math.max(0, start - 1)

            return Math.min(bars.length - 1, start + 1)
        })
    }

    return (
        <figure ref={chartRef} data-anim={chartState} className='group/chart rounded-[22px] border border-border bg-white p-5 sm:p-6'>
            <figcaption className='flex flex-wrap items-baseline justify-between gap-2'>
                <span className='text-[15px] font-semibold text-ink'>
                    {title}
                </span>

                <span className='text-[12.5px] text-muted-foreground'>
                    Hover or use the arrow keys to read a value
                </span>
            </figcaption>

            <div className='mt-6'>
                <div className='flex gap-3'>
                    <div aria-hidden className='relative h-[200px] w-11 shrink-0 text-right text-[11px] text-muted-foreground tabular-nums sm:h-[230px]'>
                        {ticks.map((tick) => (
                            <span
                                key={tick}
                                className='absolute right-0 translate-y-1/2'
                                style={{ bottom: `${(tick / top) * 100}%` }}
                            >
                                {formatNumber(tick)}
                            </span>
                        ))}
                    </div>

                    <div
                        role='group'
                        tabIndex={0}
                        aria-label={`${title}. Use the left and right arrow keys to read each value.`}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setActive((index) => index ?? bars.length - 1)}
                        onBlur={() => setActive(null)}
                        onPointerLeave={(event) => {
                            if (event.pointerType === 'mouse') setActive(null)
                        }}
                        className='relative h-[200px] min-w-0 flex-1 rounded-md focus-visible:ring-2 focus-visible:ring-moss focus-visible:ring-offset-4 focus-visible:outline-none sm:h-[230px]'
                    >
                        {ticks.map((tick) => (
                            <span
                                key={tick}
                                aria-hidden
                                className='absolute inset-x-0 h-px bg-border'
                                style={{ bottom: `${(tick / top) * 100}%` }}
                            />
                        ))}

                        <div aria-hidden className='absolute inset-0 flex items-end'>
                            {bars.map((bar, index) => (
                                <div
                                    key={index}
                                    onPointerEnter={() => setActive(index)}
                                    className='flex h-full flex-1 items-end justify-center'
                                >
                                    <span
                                        className={cn(
                                            'w-[62%] max-w-6 origin-bottom rounded-t-[4px] transition-[height,background-color] duration-500 ease-out group-data-[anim=armed]/chart:scale-y-0 motion-safe:group-data-[anim=play]/chart:animate-grow-y',
                                            active === index ? 'bg-[#7a9416]' : 'bg-moss'
                                        )}
                                        style={{
                                            height: `${(bar.value / top) * 100}%`,
                                            animationDelay: `${300 + index * 28}ms`
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        {current && active !== null && (
                            <div
                                aria-hidden
                                className={cn(
                                    'pointer-events-none absolute z-10 rounded-xl bg-ink px-3 py-2 whitespace-nowrap text-white shadow-lg',
                                    active < 2 ? 'translate-x-0' : active > bars.length - 3 ? '-translate-x-full' : '-translate-x-1/2'
                                )}
                                style={{
                                    left: active < 2
                                        ? `${(active / bars.length) * 100}%`
                                        : active > bars.length - 3
                                            ? `${((active + 1) / bars.length) * 100}%`
                                            : `${((active + 0.5) / bars.length) * 100}%`,
                                    bottom: `calc(${(current.value / top) * 100}% + 10px)`
                                }}
                            >
                                <p className='text-[15px] leading-tight font-semibold tabular-nums'>
                                    {formatNumber(current.value)}
                                </p>

                                <p className='text-[11.5px] text-white/70'>
                                    tasks · {current.label}
                                </p>
                            </div>
                        )}

                        <p aria-live='polite' className='sr-only'>
                            {current ? `${current.label}: ${formatNumber(current.value)} tasks` : ''}
                        </p>
                    </div>
                </div>

                <div aria-hidden className='mt-2.5 ml-14 flex justify-between text-[11px] text-muted-foreground'>
                    <span>{bars[0]?.label}</span>
                    <span>{bars[bars.length - 1]?.label}</span>
                </div>
            </div>

            <table className='sr-only'>
                <caption>{title}</caption>
                <thead>
                    <tr>
                        <th scope='col'>Period</th>
                        <th scope='col'>Tasks completed</th>
                    </tr>
                </thead>
                <tbody>
                    {bars.map((bar, index) => (
                        <tr key={index}>
                            <td>{bar.label}</td>
                            <td>{formatNumber(bar.value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    )
}

export const PerformanceSection: FC = (): ReactNode => {
    const [rangeKey, setRangeKey] = useState<RangeKey>('30d')

    const range = RANGES[rangeKey]
    const data = DATA[rangeKey]

    return (
        <section
            id='performance'
            aria-labelledby='performance-title'
            className='relative isolate overflow-hidden bg-white py-24 sm:py-32'
        >
            <Reveal className='mx-auto flex max-w-3xl flex-col items-center px-5 text-center'>
                <SectionBadge>Live performance</SectionBadge>

                <h2
                    id='performance-title'
                    className='mt-6 font-heading text-[clamp(2.1rem,3.1vw,2.8rem)] leading-[1.18] tracking-[-0.03em] text-balance text-ink'
                >
                    Every run, cost and outcome. <br className='max-md:hidden' />
                    In one calm view.
                </h2>

                <p className='mt-5 max-w-[54ch] text-[17px] leading-relaxed text-muted-foreground'>
                    See which agents are pulling their weight, what each task costs and where your people still step in.
                </p>
            </Reveal>

            <Reveal delay={120} className='mx-auto mt-14 w-full max-w-[1180px] px-5 sm:px-8'>
                <div className='rounded-[36px] border border-border bg-white p-2.5 shadow-[0_40px_90px_-60px_rgba(20,23,15,0.45)] sm:rounded-[42px] sm:p-3'>
                    <div className='rounded-[28px] bg-mist p-4 sm:rounded-[34px] sm:p-7'>
                        <div className='flex flex-wrap items-center justify-between gap-4'>
                            <div>
                                <h3 className='text-[17px] font-semibold text-ink'>
                                    Agent performance
                                </h3>

                                <p className='text-[13px] text-muted-foreground'>
                                    Northwind workspace · 5 agents live
                                </p>
                            </div>

                            <div role='group' aria-label='Time range' className='flex rounded-full border border-border bg-white p-1'>
                                {RANGE_KEYS.map((key) => (
                                    <button
                                        key={key}
                                        type='button'
                                        aria-pressed={rangeKey === key}
                                        onClick={() => setRangeKey(key)}
                                        className={cn(
                                            'h-9 cursor-pointer rounded-full px-4 text-[13px] font-medium transition-colors focus-visible:ring-2 focus-visible:ring-moss focus-visible:outline-none',
                                            rangeKey === key ? 'bg-ink text-white' : 'text-muted-foreground hover:text-ink'
                                        )}
                                    >
                                        {RANGES[key].label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <dl className='mt-6 grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-4'>
                            <StatTile
                                label='Tasks completed'
                                value={formatNumber(data.tasks)}
                                delta={formatDelta(data.tasksDelta, '%')}
                                comparison={range.comparison}
                                direction='up'
                            />

                            <StatTile
                                label='Success rate'
                                value={`${range.success.toFixed(1)}%`}
                                delta={formatDelta(range.successDelta, ' pts')}
                                comparison={range.comparison}
                                direction='up'
                            />

                            <StatTile
                                label='Average cost per task'
                                value={`$${range.cost.toFixed(3)}`}
                                delta={formatDelta(range.costDelta, '%')}
                                comparison={range.comparison}
                                direction='down'
                            />

                            <StatTile
                                label='Hours saved'
                                value={formatNumber(data.hours)}
                                delta={formatDelta(data.hoursDelta, '%')}
                                comparison={range.comparison}
                                direction='up'
                            />
                        </dl>

                        <div className='mt-3 grid gap-3 xl:grid-cols-[1.25fr_1fr]'>
                            <ActivityChart bars={data.bars} bucket={range.bucket} />

                            <div className='overflow-hidden rounded-[22px] border border-border bg-white'>
                                <div className='overflow-x-auto'>
                                    <table className='w-full min-w-[520px] text-left text-[13.5px]'>
                                        <caption className='px-5 pt-5 pb-3 text-left text-[15px] font-semibold text-ink sm:px-6 sm:pt-6'>
                                            Agents
                                        </caption>

                                        <thead className='text-[12px] text-muted-foreground'>
                                            <tr className='border-b border-border'>
                                                <th scope='col' className='px-5 py-2.5 font-medium sm:px-6'>Agent</th>
                                                <th scope='col' className='px-3 py-2.5 text-right font-medium'>Runs</th>
                                                <th scope='col' className='px-3 py-2.5 text-right font-medium'>Success</th>
                                                <th scope='col' className='px-3 py-2.5 text-right font-medium'>Cost</th>
                                                <th scope='col' className='px-5 py-2.5 font-medium sm:px-6'>Status</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {AGENTS.map((agent) => {
                                                const runs = Math.round(data.tasks * agent.share)

                                                return (
                                                    <tr key={agent.name} className='border-b border-border last:border-b-0'>
                                                        <th scope='row' className='px-5 py-3 font-normal sm:px-6'>
                                                            <span className='block font-medium text-ink'>{agent.name}</span>
                                                            <span className='block text-[12px] text-muted-foreground'>{agent.team}</span>
                                                        </th>

                                                        <td className='px-3 py-3 text-right text-ink tabular-nums'>
                                                            {formatNumber(runs)}
                                                        </td>

                                                        <td className='px-3 py-3 text-right text-ink tabular-nums'>
                                                            {agent.success.toFixed(1)}%
                                                        </td>

                                                        <td className='px-3 py-3 text-right text-ink tabular-nums'>
                                                            ${formatNumber(Math.round(runs * agent.costPerRun))}
                                                        </td>

                                                        <td className='px-5 py-3 sm:px-6'>
                                                            {agent.status === 'healthy' ? (
                                                                <span className='inline-flex items-center gap-1.5 rounded-full bg-sage px-2.5 py-1 text-[12px] font-medium whitespace-nowrap text-ink'>
                                                                    <CircleCheck aria-hidden className='size-3.5 text-moss' />
                                                                    Healthy
                                                                </span>
                                                            ) : (
                                                                <span className='inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-medium whitespace-nowrap text-amber-900'>
                                                                    <TriangleAlert aria-hidden className='size-3.5 text-amber-600' />
                                                                    Needs review
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>
        </section>
    )
}
